import React from 'react';
import {connect, useDispatch, useSelector} from "react-redux";
import {
    updateQuestionInSurveyAtIdx,
    validateQuestionAtIndex,
    isSurveyEditRestrictedSelector
} from "../../../../../slices/surveyEditorSlice";
import {createNewQuestionForNewType, iconMapByQType} from "../../../../../utils/SurveyUtils";
import QuestionEditText from "./QuestionEditText";
import QuestionEditOptions from "./QuestionEditOptions";
import QuestionSingleChoiceEdit from "./question_choices/QuestionSingleChoiceEdit";
import QuestionNumberEdit from "./QuestionNumberEdit";
import QuestionMultipleChoiceEdit from "./question_choices/QuestionMultipleChoiceEdit";
import QuestionMultipleOpenEdit from "./question_selects/QuestionMultipleOpenEdit";
import QuestionMatrixEdit from './question_selects/QuestionMatrixEdit';
import {withToastManager} from "react-toast-notifications";
import ErrorBoundary from "../../../../meta/ErrorBoundary";

function EditQuestionContent(props) {
    const questionTypes = [
        "singleChoice",
        "multipleChoice",
        "text", "selects",
        "jumbotron",
        "multipleOpen",
        "number"
        // "rating"
    ];
    const questionTypesNamesMap = {
        "singleChoice": "Single Choice",
        "multipleChoice": "Multiple Choice",
        "text": "Text",
        "selects": "Matrix",
        "jumbotron": "Info Box",
        "multipleOpen": "Multiple Textboxes",
        "number": "Number",
        "rating": "Rating"
    };

    const questionValid = !props.questionData.isInvalidQuestion;
    const dispatch = useDispatch();
    const surveyEditRestricted = useSelector(isSurveyEditRestrictedSelector);
    const defaultQuestion = props.survey.defaultQuestions && props.survey.defaultQuestions.find(question => question.question_id === props.questionData.question_id);
    const isNewQuestion = typeof defaultQuestion === 'undefined';

    const handleQuestionTypeChange = (event) => {
        const type = event.target.value;
        if (type && questionTypes.includes(type)) {
            const {questionData, idx} = props;
            let newQuestionData = createNewQuestionForNewType(questionData, type);
            dispatch(updateQuestionInSurveyAtIdx({idx: idx, questionData: newQuestionData}));
        }
    };

    const handleQuestionTextChange = (event) => {
        const newText = event.target.value;
        if (newText || newText === "") {
            const {questionData, idx} = props;
            let newQuestionData = JSON.parse(JSON.stringify(questionData));
            newQuestionData.question = newText;
            props.validateQuestionAtIndex(idx, newQuestionData);
        }
    };

    const buildTypeSelector = (questionTypes) => {
        let htmlSelectorButtons;
        const {questionData} = props;
        if(isNewQuestion || !surveyEditRestricted) {
            htmlSelectorButtons = questionTypes.map((type, index) => {
                const selectorId = "typeSelector_" + questionData.id + "_" + index;
                const checked = questionData.type === type;
    
                return (
                    <div key={selectorId} className="questionaire__builder__question__selector__item">
                        <input type="radio"
                               value={type}
                               id={selectorId}
                               checked={checked}
                               readOnly={true}
                               onClick={handleQuestionTypeChange}
                        />
                        <label className="" htmlFor={selectorId}>
                            <span className={"questionaire__builder__question__selector__item__icon questionaire__builder__question__selector__item__icon--" + (iconMapByQType[type] ? iconMapByQType[type] : "single")}>
                            </span> {questionTypesNamesMap[type]}
                        </label>
                    </div>
                )
            });
        }
        else {
            const selectorId = "typeSelector_" + questionData.id;
            const type = questionData.type;
            htmlSelectorButtons = (
                <div key={selectorId} className="questionaire__builder__question__selector__item">
                    <input type="radio" value={type} id={selectorId} disabled/>
                    <label className="" htmlFor={selectorId}>
                        <span className={"questionaire__builder__question__selector__item__icon questionaire__builder__question__selector__item__icon--" + (iconMapByQType[type] ? iconMapByQType[type] : "single")}>
                        </span> {questionTypesNamesMap[type]}
                    </label>
                </div>
            )
        }
        return (
            <React.Fragment>
                <ErrorBoundary boundaryName="Question Box Type Selection Menu">
                    {htmlSelectorButtons}
                </ErrorBoundary>
            </React.Fragment>
        );
    };

    const buildHtmlQuestionText = () => {
        const {questionData} = props;
        const idQuestionText = "questionText_" + questionData.id;

        return (<QuestionEditText id={idQuestionText}
                                  value={questionData.question}
                                  onChange={handleQuestionTextChange}
                                  errorMessage={"Please insert a question text."}
                                  questionValid={questionValid}
        />)
    };

    const getSpecificQuestionHTMLByType = function () {
        const {questionData, idx} = props;

        if (questionData.type) {
            switch (questionData.type) {
                case "singleChoice":
                    return (<QuestionSingleChoiceEdit questionData={questionData} idx={idx} questionValid={questionValid}/>);
                case "multipleChoice":
                    return (<QuestionMultipleChoiceEdit questionData={questionData} idx={idx} questionValid={questionValid}/>);
                case "text":
                    return null;
                case "selects":
                    return (<QuestionMatrixEdit questionData={questionData} idx={idx} questionValid={questionValid}/>);
                case "jumbotron":
                    return null;
                case "multipleOpen":
                    return <QuestionMultipleOpenEdit questionData={questionData} idx={idx} questionValid={questionValid}/>;
                case "number":
                    return <QuestionNumberEdit questionData={questionData} idx={idx} questionValid={questionValid}/>;
                case "rating":
                    return null;
                default:
                    return null;
            }
        } else {
            return null;
        }
    };

    const htmlTypeSelector = buildTypeSelector(questionTypes);
    const labelQuestionText = props.questionData.type === 'jumbotron' ? 'INFO BOX TEXT' : 'QUESTION';
    const htmlQuestionText = buildHtmlQuestionText();
    const htmlQuestionSpecificBody = getSpecificQuestionHTMLByType();

    const showSettingsSection = ["singleChoice", "multipleChoice", "selects", "multipleOpen", "number", "text", "rating"].includes(props.questionData.type);

    return (
        <div className="questionaire__builder__question__content questionaire__builder__question__content--show">
            <div className="questionaire__builder__question__subtitle">Select your question type</div>
            <div className={"questionaire__builder__question__selector" + ((isNewQuestion || !surveyEditRestricted) ? "" : " disabled")}>
                {htmlTypeSelector}
            </div>
            {props.questionData.type &&
            (
                <div className="react__fragment--single">
                    <div className="questionaire__builder__question__subtitle">{labelQuestionText}</div>
                    <div className="questionaire__builder__question__inputs">
                        <div className="questionaire__builder__col1">
                            {htmlQuestionText}
                        </div>
                        {showSettingsSection &&
                        (<div className="questionaire__builder__col2">
                            <div className="questionaire__builder__question__inputs__settings questionaire__builder__question__settings--relative">
                                <div className="questionaire__builder__question__settings--absolute">
                                    <QuestionEditOptions questionData={props.questionData} idx={props.idx}/>
                                </div>
                            </div>
                        </div>)
                        }
                    </div>
                    <ErrorBoundary boundaryName="Question Edit Specific Component">
                        {htmlQuestionSpecificBody}
                    </ErrorBoundary>
                    <div className="questionaire__builder__question__inputs">
                        {showSettingsSection &&
                        (<div className="questionaire__builder__col1">
                            <div className="questionaire__builder__question__inputs__settings questionaire__builder__question__inputs__settings--mobile">
                                <QuestionEditOptions questionData={props.questionData} idx={props.idx}/>
                            </div>
                        </div>)
                        }
                    </div>
                </div>
            )
            }
        </div>
    )
}

function mapStateToProps(state) {
    const {survey} = state;
    return {survey};
}

const mapDispatchToProps = {validateQuestionAtIndex};

export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(EditQuestionContent));